import { Button, Title } from "lola-framework-ui-test";
import { useTranslation } from "react-i18next";
import type { ICardPaymentMethod } from "../interfaces/payment-method";
import type { FundingMethod } from "../utils/catalog";
import {
  fundingCardDisplayName,
} from "../utils/resolve-funding-card";
import {
  unavailableCardReason,
  type UnavailableCardReason,
} from "../utils/card-funding-availability";
import { BodyCopy } from "./BodyCopy";
import { CloseLight } from "../assets/navIcons";

const UNAVAILABLE_REASON_KEY: Record<UnavailableCardReason, string> = {
  creditOnDebitRoute: "payment.unavailableCredit",
  debitOnCreditRoute: "payment.unavailableDebit",
  unclassified: "payment.unavailableUnclassified",
  generic: "payment.unavailableGeneric",
  railRejected: "payment.unavailableRailRejected",
  railTemporarilyUnavailable: "payment.unavailableRailTemporary",
};

export interface UnavailableFundingCardSheetProps {
  card: ICardPaymentMethod;
  available: readonly FundingMethod[];
  bodyCopy: string;
  backgroundBtn: string;
  textColorBtn: string;
  onDismiss: () => void;
  /** Picker shows a close control in the header; confirm overlay uses primary CTA only. */
  showHeaderClose?: boolean;
  /** Pay-time code from RaaS. When set, the sheet does not reclassify the card. */
  reason?: UnavailableCardReason;
  /** Opens the funding picker. Shown only when the caller can switch methods. */
  onChangeMethod?: () => void;
}

export const UnavailableFundingCardSheet: React.FC<
  UnavailableFundingCardSheetProps
> = ({
  card,
  available,
  bodyCopy,
  backgroundBtn,
  textColorBtn,
  onDismiss,
  showHeaderClose = true,
  reason,
  onChangeMethod,
}) => {
  const { t } = useTranslation();
  const displayName = fundingCardDisplayName(card);
  const shown = reason ?? unavailableCardReason(card, available);

  return (
    <section className="select-payer-slot amount-limit-slot">
      <header className="payout-details-header">
        <span className="payout-header-action" aria-hidden="true" />
        <span />
        {showHeaderClose ? (
          <button
            type="button"
            className="payout-header-action payout-close-btn"
            aria-label={t("common.cancel")}
            onClick={onDismiss}
          >
            <CloseLight size={36} colors={[bodyCopy, bodyCopy]} />
          </button>
        ) : (
          <span className="payout-header-action" aria-hidden="true" />
        )}
      </header>
      <Title
        title={t("payment.unavailableTitle")}
        align="center"
        color={bodyCopy}
      />
      <BodyCopy className="amount-limit-text" textColor={bodyCopy}>
        <b>{displayName}</b>
      </BodyCopy>
      <BodyCopy className="amount-limit-text" textColor={bodyCopy}>
        {t(UNAVAILABLE_REASON_KEY[shown])}
      </BodyCopy>
      {onChangeMethod ? (
        <Button
          size="large"
          variant="cancel"
          color={bodyCopy}
          onClick={onChangeMethod}
        >
          {t("payment.changeMethod")}
        </Button>
      ) : null}
      <Button
        size="large"
        background={backgroundBtn}
        color={textColorBtn}
        onClick={onDismiss}
      >
        {t("amountLimit.gotIt")}
      </Button>
    </section>
  );
};
